import React, { useState } from 'react';
import PropTypes from 'prop-types';
import BootstrapSpinner from 'react-bootstrap/Spinner';
import { InlineLinkButton } from '../../commonComponents/InlineLinkButton';
import { useGetPromotionPayouts } from '../../../domain/Promotions';
import { formatPromotionDate } from './utils';
import { COLORS, PAYOUT_GRID_COLUMNS, promotionPropType } from './constants';

const ChevronIcon = ({ expanded }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    style={{
      transform: expanded ? 'rotate(180deg)' : 'none',
      transition: 'transform 0.15s',
    }}
  >
    <path
      d="M6 9l6 6 6-6"
      stroke={COLORS.secondary}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

ChevronIcon.propTypes = { expanded: PropTypes.bool.isRequired };

const cellStyle = {
  padding: '0 15px',
  display: 'flex',
  alignItems: 'center',
  minWidth: 0,
};

const viewLinkStyle = {
  textDecoration: 'underline',
  color: COLORS.text,
  fontSize: 14,
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

// Bold, non-underlined variant for the row's action buttons (Add Payout, Edit).
const actionLinkStyle = {
  ...viewLinkStyle,
  fontWeight: 700,
  fontSize: 16,
  textDecoration: 'none',
};

const PayoutLink = ({ label, onClick }) => {
  if (!onClick) {
    return <span style={{ fontSize: 14, color: COLORS.text }}>{label}</span>;
  }

  return <InlineLinkButton onClicked={onClick}>{label}</InlineLinkButton>;
};

PayoutLink.propTypes = {
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func,
};

PayoutLink.defaultProps = { onClick: undefined };

export const PromotionRow = ({
  promotion,
  onAddPayout,
  onReactivate,
  onEdit,
  onViewReport,
}) => {
  const [expanded, setExpanded] = useState(false);
  const { payouts, isLoading } = useGetPromotionPayouts(promotion.id, {
    enabled: expanded,
  });

  return (
    <div>
      {/* Collapsed promotion header row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          backgroundColor: COLORS.rowBg,
          minHeight: 53,
          padding: '0 15px',
          gap: 16,
        }}
      >
        <div
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          onClick={() => setExpanded((prev) => !prev)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setExpanded((prev) => !prev);
            }
          }}
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 12,
            flex: 1,
            minWidth: 0,
            cursor: 'pointer',
          }}
        >
          <span style={{ fontSize: 30, color: COLORS.text, lineHeight: '33px' }}>
            {promotion.name}
          </span>
          <span style={{ fontSize: 14, color: COLORS.text, whiteSpace: 'nowrap' }}>
            Max Value {promotion.maxValueDisplay}
          </span>
          {promotion.isDisabled && (
            <span style={{ fontSize: 12, color: '#999', whiteSpace: 'nowrap' }}>
              Disabled
            </span>
          )}
        </div>

        {promotion.isDisabled ? (
          <button type="button" style={actionLinkStyle} onClick={() => onReactivate?.(promotion)}>
            Re-activate
          </button>
        ) : (
          <button type="button" style={actionLinkStyle} onClick={() => onAddPayout?.(promotion)}>
            Add Payout
          </button>
        )}
        <button type="button" style={actionLinkStyle} onClick={() => onEdit?.(promotion)}>
          Edit
        </button>
        <button
          type="button"
          aria-label={expanded ? 'Collapse promotion' : 'Expand promotion'}
          aria-expanded={expanded}
          style={{ ...viewLinkStyle, display: 'flex', alignItems: 'center' }}
          onClick={() => setExpanded((prev) => !prev)}
        >
          <ChevronIcon expanded={expanded} />
        </button>
      </div>

      {/* Expanded payout batches */}
      {expanded && (
        <div>
          {isLoading && (
            <div style={{ padding: 16, textAlign: 'center' }}>
              <BootstrapSpinner animation="border" size="sm" variant="info" role="status" />
            </div>
          )}

          {!isLoading && payouts.length === 0 && (
            <div style={{ padding: '16px 15px', fontSize: 14, color: COLORS.text }}>
              No payouts have been submitted for this promotion yet.
            </div>
          )}

          {!isLoading &&
            payouts.map((batch) => (
              <div
                key={batch.batchId}
                style={{
                  display: 'grid',
                  gridTemplateColumns: PAYOUT_GRID_COLUMNS,
                  minHeight: 61,
                  border: `1px solid ${COLORS.border}`,
                }}
              >
                <div style={{ ...cellStyle, fontWeight: 700, fontSize: 15 }}>
                  {formatPromotionDate(batch.submittedAt)}
                </div>
                <div style={{ ...cellStyle, fontSize: 15 }}>{batch.uploadedBy}</div>
                <div style={{ ...cellStyle, justifyContent: 'center' }}>
                  <PayoutLink
                    label="View"
                    onClick={
                      onViewReport
                        ? () => onViewReport(promotion, batch, 'succeeded')
                        : undefined
                    }
                  />
                </div>
                <div style={{ ...cellStyle, justifyContent: 'center' }}>
                  {batch.failedCount === null || batch.failedCount === undefined ? (
                    <span style={{ fontSize: 14, color: COLORS.text }}>n/a</span>
                  ) : (
                    <PayoutLink
                      label="View"
                      onClick={
                        onViewReport
                          ? () => onViewReport(promotion, batch, 'failed')
                          : undefined
                      }
                    />
                  )}
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

PromotionRow.propTypes = {
  promotion: promotionPropType.isRequired,
  onAddPayout: PropTypes.func,
  onReactivate: PropTypes.func,
  onEdit: PropTypes.func,
  onViewReport: PropTypes.func,
};

PromotionRow.defaultProps = {
  onAddPayout: undefined,
  onReactivate: undefined,
  onEdit: undefined,
  onViewReport: undefined,
};
